/**
 * Content Language Metadata Service
 * 
 * Manages language metadata for surveys, tasks, and other content in localStorage.
 * This service provides a centralized way to store and retrieve language information
 * for content items without requiring database changes.
 */

type SupportedLanguage = 'en' | 'zh';
type ContentType = 'survey' | 'task' | 'idea' | 'prerelease' | 'assets' | 'response';

interface ContentLanguageMetadata {
  contentId: string;
  contentType: ContentType;
  language: SupportedLanguage;
  createdAt: number;
}

export class ContentLanguageMetadataService {
  /**
   * Store language metadata for a content item
   */
  static setContentLanguage(
    contentType: ContentType,
    contentId: string,
    language: SupportedLanguage
  ): void {
    try {
      const key = this.getStorageKey(contentType, contentId);
      const metadata: ContentLanguageMetadata = {
        contentId,
        contentType,
        language,
        createdAt: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(metadata));
      console.log(`✅ Stored ${contentType} language metadata:`, { contentId, language });
    } catch (error) {
      console.error(`Failed to store ${contentType} language metadata:`, error);
    }
  }

  /**
   * Get language metadata for a content item
   */
  static getContentLanguage(
    contentType: ContentType,
    contentId: string
  ): SupportedLanguage | null {
    try {
      const key = this.getStorageKey(contentType, contentId);
      const stored = localStorage.getItem(key);
      
      if (!stored) {
        return null;
      }

      const metadata: ContentLanguageMetadata = JSON.parse(stored);
      return metadata.language;
    } catch (error) {
      console.error(`Failed to retrieve ${contentType} language metadata:`, error);
      return null;
    }
  }

  /**
   * Get full metadata for a content item
   */
  static getContentMetadata(
    contentType: ContentType,
    contentId: string
  ): ContentLanguageMetadata | null {
    try {
      const key = this.getStorageKey(contentType, contentId);
      const stored = localStorage.getItem(key);
      
      if (!stored) {
        return null;
      }

      return JSON.parse(stored);
    } catch (error) {
      console.error(`Failed to retrieve ${contentType} metadata:`, error);
      return null;
    }
  }

  /**
   * Remove language metadata for a content item
   */
  static removeContentLanguage(
    contentType: ContentType,
    contentId: string
  ): void {
    try {
      const key = this.getStorageKey(contentType, contentId);
      localStorage.removeItem(key);
      console.log(`🗑️ Removed ${contentType} language metadata:`, contentId);
    } catch (error) {
      console.error(`Failed to remove ${contentType} language metadata:`, error);
    }
  }

  /**
   * Get all content items of a specific type with their languages
   */
  static getAllContentByType(contentType: ContentType): ContentLanguageMetadata[] {
    try {
      const prefix = `content_language_${contentType}_`;
      const items: ContentLanguageMetadata[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const metadata: ContentLanguageMetadata = JSON.parse(stored);
              items.push(metadata);
            } catch (parseError) {
              console.warn(`Failed to parse metadata for key ${key}:`, parseError);
            }
          }
        }
      }

      return items;
    } catch (error) {
      console.error(`Failed to get all ${contentType} metadata:`, error);
      return [];
    }
  }

  /**
   * Check if content has language metadata
   */
  static hasContentLanguage(
    contentType: ContentType,
    contentId: string
  ): boolean {
    const key = this.getStorageKey(contentType, contentId);
    return localStorage.getItem(key) !== null;
  }

  /**
   * Bulk set language for multiple content items
   */
  static bulkSetContentLanguage(
    contentType: ContentType,
    items: Array<{ id: string; language: SupportedLanguage }>
  ): void {
    items.forEach(item => {
      this.setContentLanguage(contentType, item.id, item.language);
    });
  }

  /**
   * Clear all language metadata for a specific content type
   */
  static clearContentType(contentType: ContentType): void {
    try {
      const prefix = `content_language_${contentType}_`;
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`🗑️ Cleared all ${contentType} language metadata (${keysToRemove.length} items)`);
    } catch (error) {
      console.error(`Failed to clear ${contentType} language metadata:`, error);
    }
  }

  /**
   * Generate storage key for content language metadata
   */
  private static getStorageKey(contentType: ContentType, contentId: string): string {
    return `content_language_${contentType}_${contentId}`;
  }

  /**
   * Get statistics about stored language metadata
   */
  static getStats(): Record<ContentType, number> {
    const stats: Record<string, number> = {
      survey: 0,
      task: 0,
      idea: 0,
      prerelease: 0,
      assets: 0,
      response: 0
    };

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('content_language_')) {
          const parts = key.split('_');
          if (parts.length >= 3) {
            const contentType = parts[2];
            if (contentType in stats) {
              stats[contentType]++;
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to get language metadata stats:', error);
    }

    return stats as Record<ContentType, number>;
  }
}
